import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Download, Smartphone, CheckCircle, Share } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const Install = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
    }

    const ua = navigator.userAgent;
    setIsIOS(/iPad|iPhone|iPod/.test(ua));

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setDeferredPrompt(null);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Helmet>
        <title>تثبيت التطبيق | NurseryNet</title>
        <meta name="description" content="ثبّت تطبيق NurseryNet على هاتفك للوصول السريع وتجربة سلسة لإدارة الحضانة." />
        <link rel="canonical" href="https://nurserynet.cloud/install" />
        <meta property="og:title" content="تثبيت تطبيق NurseryNet" />
        <meta property="og:description" content="ثبّت NurseryNet على هاتفك بسهولة كتطبيق ويب تقدمي." />
        <meta property="og:url" content="https://nurserynet.cloud/install" />
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center space-y-8"
      >
        <img src="/pwa-icon-512.png" alt="شعار تطبيق NurseryNet لإدارة الحضانات" className="w-28 h-28 mx-auto rounded-3xl shadow-elevated" />

        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-foreground">NurseryNet — تثبيت التطبيق</h1>
          <p className="text-muted-foreground">ثبّت التطبيق على جهازك للوصول السريع وتجربة أفضل</p>
        </div>

        {isInstalled ? (
          <div className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-primary/10">
            <CheckCircle className="w-12 h-12 text-primary" />
            <p className="text-primary font-semibold text-lg">التطبيق مثبّت بالفعل! 🎉</p>
          </div>
        ) : isIOS ? (
          <div className="space-y-4 p-6 rounded-2xl bg-muted">
            <Share className="w-10 h-10 text-primary mx-auto" />
            <p className="font-semibold text-foreground">لتثبيت التطبيق على iPhone:</p>
            <ol className="text-sm text-muted-foreground space-y-2 text-right">
              <li>1. اضغط على زر المشاركة <Share className="inline w-4 h-4" /> في المتصفح</li>
              <li>2. اختر "إضافة إلى الشاشة الرئيسية"</li>
              <li>3. اضغط "إضافة"</li>
            </ol>
          </div>
        ) : deferredPrompt ? (
          <Button onClick={handleInstall} size="lg" className="w-full text-lg gap-3 h-14">
            <Download className="w-6 h-6" />
            تثبيت التطبيق
          </Button>
        ) : (
          <div className="space-y-4 p-6 rounded-2xl bg-muted">
            <Smartphone className="w-10 h-10 text-primary mx-auto" />
            <p className="text-sm text-muted-foreground">
              افتح هذه الصفحة من متصفح الهاتف (Chrome أو Safari) لتثبيت التطبيق
            </p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4 pt-4">
          {[
            { icon: "⚡", label: "سريع" },
            { icon: "📱", label: "يعمل بدون نت" },
            { icon: "🔔", label: "إشعارات" },
          ].map((f) => (
            <div key={f.label} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card shadow-card">
              <span className="text-2xl">{f.icon}</span>
              <span className="text-xs text-muted-foreground">{f.label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Install;
